import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Trash2, RefreshCw, Activity, Globe } from 'lucide-react';

// ── Activity Log ─────────────────────────────────────────────────────────────

const ACTION_COLORS = {
  create:        'bg-green-100 text-green-700',
  update:        'bg-blue-100 text-blue-700',
  delete:        'bg-red-100 text-red-700',
  status_change: 'bg-purple-100 text-purple-700',
  login:         'bg-gray-100 text-gray-600',
  bulk_action:   'bg-orange-100 text-orange-700',
  enroll:        'bg-teal-100 text-teal-700',
};

const RESOURCE_TYPES = ['', 'application', 'student', 'cohort', 'intake', 'user', 'role', 'config', 'email', 'test'];

function ActivityTab({ auth }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (resourceFilter) params.append('resource_type', resourceFilter);
      const res = await axios.get(`${API}/audit-logs?${params}&limit=500`, auth());
      setLogs(res.data);
    } catch {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [resourceFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleClear = async () => {
    if (!window.confirm('Clear all activity logs? This cannot be undone.')) return;
    try {
      const res = await axios.delete(`${API}/audit-logs`, auth());
      toast.success(`Cleared ${res.data.deleted} records`);
      setLogs([]);
    } catch {
      toast.error('Failed to clear activity logs');
    }
  };

  const filtered = logs.filter(l => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return l.summary?.toLowerCase().includes(q) || l.actor_email?.toLowerCase().includes(q) || l.resource_id?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap items-center">
        <Input placeholder="Search summary, actor, resource ID…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-xs text-sm h-8" />
        <div className="flex gap-2 flex-wrap">
          {RESOURCE_TYPES.map(rt => (
            <button key={rt} onClick={() => setResourceFilter(rt)}
              className={`px-3 py-1 rounded-full text-xs font-medium border-2 capitalize ${resourceFilter === rt ? 'border-primary bg-primary text-white' : 'border-transparent bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {rt || 'All'}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{filtered.length} records</span>
          <Button variant="outline" size="sm" onClick={fetchLogs}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          <Button variant="destructive" size="sm" onClick={handleClear}><Trash2 className="w-3.5 h-3.5 mr-1" /> Clear All</Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">No activity logs found.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Summary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-xs">{log.actor_email || <span className="text-muted-foreground italic">system</span>}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600'}`}>
                        {log.action}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-medium text-muted-foreground capitalize">{log.resource_type}</span>
                      {log.resource_id && <span className="text-xs text-muted-foreground ml-1 opacity-60">({log.resource_id.slice(0, 8)}…)</span>}
                    </TableCell>
                    <TableCell className="text-sm">{log.summary}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Raw HTTP Log ──────────────────────────────────────────────────────────────

const METHOD_COLORS = {
  POST:   'bg-green-100 text-green-700',
  PUT:    'bg-blue-100 text-blue-700',
  PATCH:  'bg-yellow-100 text-yellow-700',
  DELETE: 'bg-red-100 text-red-700',
};

const STATUS_COLORS = (code) => {
  if (code >= 500) return 'text-red-600 font-semibold';
  if (code >= 400) return 'text-orange-600 font-semibold';
  if (code >= 200 && code < 300) return 'text-green-700';
  return 'text-gray-600';
};

const PURGE_OPTIONS = [
  { label: 'Older than 30 days', days: 30 },
  { label: 'Older than 60 days', days: 60 },
  { label: 'Older than 90 days', days: 90 },
  { label: 'Clear all', days: null },
];

function RawHttpTab({ auth }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [methodFilter, setMethodFilter] = useState('');
  const [actorSearch, setActorSearch] = useState('');
  const [pathSearch, setPathSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 500 });
      if (methodFilter) params.append('method', methodFilter);
      if (actorSearch.trim()) params.append('actor_email', actorSearch.trim());
      if (pathSearch.trim()) params.append('path_contains', pathSearch.trim());
      if (statusFilter) params.append('status_code', statusFilter);
      const res = await axios.get(`${API}/http-request-logs?${params}`, auth());
      setLogs(res.data);
    } catch {
      toast.error('Failed to load HTTP logs');
    } finally {
      setLoading(false);
    }
  }, [methodFilter, actorSearch, pathSearch, statusFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handlePurge = async (days) => {
    const label = days ? `logs older than ${days} days` : 'all HTTP logs';
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      const params = days ? `?older_than_days=${days}` : '';
      const res = await axios.delete(`${API}/http-request-logs${params}`, auth());
      toast.success(`Deleted ${res.data.deleted} records`);
      fetchLogs();
    } catch {
      toast.error('Failed to purge HTTP logs');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap items-center">
        <div className="flex gap-1.5">
          {['', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
            <button key={m} onClick={() => setMethodFilter(m)}
              className={`px-2.5 py-1 rounded text-xs font-mono font-medium border ${methodFilter === m ? 'bg-primary text-white border-primary' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}>
              {m || 'All'}
            </button>
          ))}
        </div>
        <Input placeholder="Filter by actor email…" value={actorSearch} onChange={e => setActorSearch(e.target.value)} className="max-w-[200px] text-sm h-8" />
        <Input placeholder="Filter by path…" value={pathSearch} onChange={e => setPathSearch(e.target.value)} className="max-w-[200px] text-sm h-8" />
        <Input placeholder="Status code…" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="max-w-[100px] text-sm h-8" type="number" />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{logs.length} records</span>
          <Button variant="outline" size="sm" onClick={fetchLogs}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          <div className="relative group">
            <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Purge
            </Button>
            <div className="absolute right-0 top-full mt-1 z-10 hidden group-hover:block bg-white border rounded-lg shadow-lg py-1 w-44">
              {PURGE_OPTIONS.map(opt => (
                <button key={opt.label} onClick={() => handlePurge(opt.days)}
                  className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50">
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-12">Loading…</div>
      ) : logs.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">No HTTP request logs found.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Path</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${METHOD_COLORS[log.method] || 'bg-gray-100 text-gray-600'}`}>
                        {log.method}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-gray-700 max-w-[280px] truncate" title={log.path}>
                      {log.path}
                    </TableCell>
                    <TableCell className={`text-xs font-mono ${STATUS_COLORS(log.status_code)}`}>
                      {log.status_code}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {log.duration_ms}ms
                    </TableCell>
                    <TableCell className="text-xs">
                      {log.actor_email || <span className="text-muted-foreground italic">unauthenticated</span>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {log.ip_address || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function AdminAuditLogsPage() {
  const [tab, setTab] = useState('activity');
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const tabs = [
    { id: 'activity', label: 'Activity Log', Icon: Activity, desc: 'Business-language record of all admin actions' },
    { id: 'http',     label: 'Raw HTTP Log', Icon: Globe,    desc: 'Every mutating API request — method, path, status, duration' },
  ];

  return (
    <div>
      <div className="bg-white border-b px-6 py-4">
        <h1 className="text-xl font-playfair font-bold text-primary">Audit Logs</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          {tabs.find(t => t.id === tab)?.desc}
        </p>
        <div className="flex gap-1 mt-3">
          {tabs.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-t text-sm font-medium border-b-2 transition-colors ${tab === id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {tab === 'activity' && <ActivityTab auth={auth} />}
        {tab === 'http'     && <RawHttpTab  auth={auth} />}
      </div>
    </div>
  );
}
